import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, MapPin, ShieldCheck, Loader2 } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '../components/ui/breadcrumb';
import { BRAND } from '../lib/siteConfig';
import { breadcrumbSchema } from '../lib/seoSchemas';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocalCareIndexPage() {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/cities`)
      .then((res) => setCities(res.data || []))
      .catch(() => setCities([]))
      .finally(() => setLoading(false));
  }, []);

  const schema = breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Home care near you', path: '/home-care' },
  ]);

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="local-care-index-page">
      <Seo
        title={`In-Home Care Near You — Verified Clinicians by City | ${BRAND}`}
        description="Find verified, background-checked nurses, aides, and caregivers for in-home care in your city. Browse local home-care availability across the United States on CaraLoom."
        path="/home-care"
        keywords={['home care near me', 'in-home care by city', 'verified nurse near me', 'local home care']}
        schema={schema}
      />
      <PublicNavbar />

      <div className="cn-container pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Home care near you</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <section className="cn-container pt-8 pb-10">
        <div className="max-w-2xl">
          <div className="cn-eyebrow mb-3 flex items-center gap-2"><MapPin className="w-4 h-4 text-cn-accent" /> Local home care</div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-5">Verified home care, city by city.</h1>
          <p className="text-lg text-cn-text-muted leading-relaxed">
            CaraLoom connects families with verified, background-checked clinicians for in-home care across the country.
            Choose your city to see local availability — or get matched instantly by our AI assistant.
          </p>
        </div>
      </section>

      <section className="cn-container pb-20">
        {loading ? (
          <div className="flex items-center gap-2 text-cn-text-muted py-16 justify-center" data-testid="local-index-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading cities…
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {cities.map((c) => (
              <Link
                key={c.slug}
                to={`/home-care/${c.slug}`}
                className="cn-card cn-card-hover rounded-2xl p-5 flex items-center justify-between group"
                data-testid={`city-link-${c.slug.replace('/', '-')}`}
              >
                <div>
                  <div className="font-display font-semibold text-cn-text">{c.city}, {c.state}</div>
                  <div className="text-sm text-cn-text-muted mt-0.5">
                    {c.count} verified clinician{c.count === 1 ? '' : 's'}{c.min_rate ? ` · from $${c.min_rate}/hr` : ''}
                  </div>
                </div>
                <ArrowRight className="w-4 h-4 text-cn-text-muted group-hover:text-cn-primary transition-colors" />
              </Link>
            ))}
          </div>
        )}
      </section>

      <section className="cn-container pb-20">
        <div className="rounded-3xl bg-cn-primary text-white p-10 md:p-14 text-center">
          <div className="inline-flex items-center gap-2 text-white/80 text-sm mb-3">
            <ShieldCheck className="w-4 h-4" /> Verified · Background-checked · Insured
          </div>
          <h2 className="cn-display text-3xl md:text-4xl text-white mb-3">Don&rsquo;t see your city?</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7">
            We&rsquo;re adding clinicians nationwide every week. Tell our AI assistant what you need and we&rsquo;ll find your match.
          </p>
          <Link to="/auth" className="cn-btn bg-white text-cn-primary hover:bg-cn-soft" data-testid="local-index-cta">
            Get matched <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
